/**
 * API Service Layer for HR Management System
 * Provides centralized API calls to backend
 */

const API_BASE_URL = '/api';

console.log('API_BASE_URL:', API_BASE_URL);
console.log('Environment variables:', (import.meta as any).env);

function redirectToCrmLogin() {
  localStorage.removeItem('auth_user');
  localStorage.removeItem('tenant_id');
  localStorage.removeItem('selected_tenant_id');
  localStorage.removeItem('modular_permissions');
  localStorage.removeItem('allowed_modules');
  sessionStorage.removeItem('cognito_pkce_verifier');

  const crmUrl = (import.meta as any).env.VITE_CRM_URL;
  const { hostname, protocol } = window.location;
  const target = crmUrl
    ? `${crmUrl.replace(/\/$/, '')}/login`
    : hostname.includes('dealbox-')
      ? `${protocol}//${hostname.replace(/dealbox-[a-z]+-/, 'dealbox-crm-')}/login`
      : `http://${hostname}:3000/login`;
  window.location.href = target;
}

// Helper function to build query string, filtering out undefined/null/empty values
function buildQueryString(params?: Record<string, any>): string {
  if (!params) return '';
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
  );
  return new URLSearchParams(filteredParams as Record<string, string>).toString();
}

// Generic API request handler
async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Remove leading slash from endpoint to avoid double slashes
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  const url = `${API_BASE_URL}/${cleanEndpoint}`;

  console.log('Making API request to:', url);

  const tenantId = localStorage.getItem('tenant_id') || localStorage.getItem('selected_tenant_id');

  const defaultOptions: RequestInit = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);
    console.log('Response status:', response.status, 'for', url);

    if (!response.ok) {
      if (response.status === 401) {
        redirectToCrmLogin();
      }
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      const errorMessage = typeof error.detail === 'string' ? error.detail :
        error.message ||
        JSON.stringify(error.detail) ||
        `API Error: ${response.status}`;
      throw new Error(errorMessage);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T;
    }

    return await response.json();
  } catch (error) {
    console.error(`API Request failed: ${endpoint}`, error);
    // Ensure error is always an Error object with a message
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(String(error));
  }
}

// ====================
// EMPLOYEE APIs
// ====================

export const employeeApi = {
  getAll: (params?: {
    status?: string;
    department_id?: string;
    search?: string;
    offset?: number;
    limit?: number;
  }) => {
    const query = buildQueryString(params);
    return apiRequest(`/employees/?${query}`, { method: 'GET' });
  },

  getCombined: (params?: {
    status?: string;
    department_id?: string;
    search?: string;
    source?: string;
    offset?: number;
    limit?: number;
  }) => {
    const query = buildQueryString(params);
    return apiRequest(`/employees/combined?${query}`, { method: 'GET' });
  },

  getById: (id: string) =>
    apiRequest(`/employees/${id}`, { method: 'GET' }),

  create: (data: any) =>
    apiRequest('/employees/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    apiRequest(`/employees/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiRequest(`/employees/${id}`, { method: 'DELETE' }),

  checkDeletionAssignments: (id: string) =>
    apiRequest<{
      can_delete: boolean;
      deal_names: string[];
      activity_names: string[];
      assignments: string[];
      message: string;
    }>(`/employees/${id}/deletion-check`, { method: 'GET' }),

  getStats: (id: string) =>
    apiRequest(`/employees/${id}/stats`, { method: 'GET' }),

  getReviewers: () =>
    apiRequest('/employees/reviewers', { method: 'GET' }),

  getRoles: () =>
    apiRequest('/employees/roles', { method: 'GET' }),

  resetPassword: (id: string) =>
    apiRequest<{ temporary_password: string }>(`/employees/${id}/reset-password`, { method: 'POST' }),
};

// ====================
// DEPARTMENT APIs
// ====================

export const departmentApi = {
  getAll: () =>
    apiRequest('/employees/departments', { method: 'GET' }),

  getById: (id: string) =>
    apiRequest(`/employees/departments/${id}`, { method: 'GET' }),

  create: (data: any) =>
    apiRequest('/employees/departments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: any) =>
    apiRequest(`/employees/departments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiRequest(`/employees/departments/${id}`, { method: 'DELETE' }),
};

// ====================
// LEAVE APIs
// ====================

export const leaveApi = {
  policies: {
    getAll: () => apiRequest('/leaves/policies', { method: 'GET' }),
    getById: (id: string) => apiRequest(`/leaves/policies/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/leaves/policies', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/leaves/policies/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/leaves/policies/${id}`, { method: 'DELETE' }),
  },

  requests: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/leaves/requests?${query}`, { method: 'GET' });
    },
    getTeamRequests: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/leaves/requests/team/approvals?${query}`, { method: 'GET' });
    },
    getEmployeesOverview: () => apiRequest('/leaves/employees/overview', { method: 'GET' }),
    getById: (id: string) => apiRequest(`/leaves/requests/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/leaves/requests', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/leaves/requests/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    approve: (id: string) =>
      apiRequest(`/leaves/requests/${id}/approve`, { method: 'POST' }),
    reject: (id: string) =>
      apiRequest(`/leaves/requests/${id}/reject`, { method: 'POST' }),
  },

  holidays: {
    getAll: (params?: { year?: number; location?: string }) => {
      const query = buildQueryString(params);
      return apiRequest(`/leaves/holidays?${query}`, { method: 'GET' });
    },
    create: (data: any) => apiRequest('/leaves/holidays', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/leaves/holidays/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/leaves/holidays/${id}`, { method: 'DELETE' }),
    sync: (country: string, year: number) => apiRequest(`/leaves/holidays/sync/${country}/${year}`, { method: 'GET' }),
  },
};

// ====================
// PERFORMANCE APIs
// ====================

export const performanceApi = {
  reviews: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/performance/reviews?${query}`, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/performance/reviews/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/performance/reviews', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/performance/reviews/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/performance/reviews/${id}`, { method: 'DELETE' }),
  },

  goals: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/performance/goals?${query}`, { method: 'GET' });
    },
    create: (data: any) => apiRequest('/performance/goals', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/performance/goals/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/performance/goals/${id}`, { method: 'DELETE' }),
    addKPI: (goalId: string, kpi: any) => apiRequest(`/performance/goals/${goalId}/kpis`, { method: 'POST', body: JSON.stringify(kpi) }),
    removeKPI: (goalId: string, kpiId: string) => apiRequest(`/performance/goals/${goalId}/kpis/${kpiId}`, { method: 'DELETE' }),
    updateKPIProgress: (goalId: string, kpiId: string, progress: number) =>
      apiRequest(`/performance/goals/${goalId}/kpi/${kpiId}/progress`, {
        method: 'PATCH',
        body: JSON.stringify({ kpi_id: kpiId, progress })
      }),
  },

  feedback: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/performance/feedback?${query}`, { method: 'GET' });
    },
    create: (data: any) => apiRequest('/performance/feedback', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/performance/feedback/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/performance/feedback/${id}`, { method: 'DELETE' }),
  },

  // Dedicated Employee Feedback Entries (My Feedbacks page)
  employeeFeedbacks: {
    getAll: (params?: {
      given_to_id?: string;
      given_by_id?: string;
      feedback_type?: string;
      offset?: number;
      limit?: number;
    }) => {
      const query = buildQueryString(params);
      return apiRequest(`/performance/employee-feedbacks?${query}`, { method: 'GET' });
    },
    create: (data: {
      subject: string;
      description: string;
      given_by_id: string;
      given_by_name: string;
      given_to_id: string;
      given_to_name: string;
      feedback_type: 'escalation' | 'feedback' | string;
      feedback_date: string;
      score: number;
      deal_id?: string;
      deal_name?: string;
    }) => apiRequest('/performance/employee-feedbacks', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/performance/employee-feedbacks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/performance/employee-feedbacks/${id}`, { method: 'DELETE' }),
  },

  // CRM Deals — for the optional deal dropdown in the feedback form
  crmDeals: {
    getAll: () => apiRequest('/performance/crm-deals', { method: 'GET' }),
  },

  ktSessions: {
    getAll: (params?: { knowledge_transfer_id?: string; status?: string }) => {
      const query = buildQueryString(params);
      return apiRequest(`/performance/kt-sessions?${query}`, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/performance/kt-sessions/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/performance/kt-sessions', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/performance/kt-sessions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/performance/kt-sessions/${id}`, { method: 'DELETE' }),
    getProgress: (ktId: string) => apiRequest(`/performance/kt-progress/${ktId}`, { method: 'GET' }),
  },

  // Manager & Team endpoints
  getTeamOverview: (params: { manager_id: string; year: number }) => {
    const query = buildQueryString(params);
    return apiRequest(`/performance/team-overview?${query}`, { method: 'GET' });
  },

  getEmployeeDetails: (employeeId: string, year: number) =>
    apiRequest(`/performance/employee-details/${employeeId}/${year}`, { method: 'GET' }),

  approveGoal: (goalId: string, data?: { manager_comments?: string }) =>
    apiRequest(`/performance/goals/${goalId}/approve`, {
      method: 'PATCH',
      body: JSON.stringify(data || {})
    }),

  rejectGoal: (goalId: string, data?: { manager_comments?: string }) =>
    apiRequest(`/performance/goals/${goalId}/reject`, {
      method: 'PATCH',
      body: JSON.stringify(data || {})
    }),

  scheduleOneOnOne: (goalId: string, data: { scheduled_date: string; notes?: string }) =>
    apiRequest(`/performance/goals/${goalId}/schedule-one-on-one`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  completeCycle: (goalId: string) =>
    apiRequest(`/performance/goals/${goalId}/complete-cycle`, { method: 'PATCH' }),
};

// ====================
// PAYROLL APIs
// ====================

export const payrollApi = {
  profiles: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/payroll/profiles?${query}`, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/payroll/profiles/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/payroll/profiles', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/payroll/profiles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/payroll/profiles/${id}`, { method: 'DELETE' }),
  },

  runs: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/payroll/runs?${query}`, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/payroll/runs/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/payroll/runs', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/payroll/runs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  },

  benefits: {
    getAll: () => apiRequest('/payroll/benefits', { method: 'GET' }),
    create: (data: any) => apiRequest('/payroll/benefits', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/payroll/benefits/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/payroll/benefits/${id}`, { method: 'DELETE' }),
  },

  deductions: {
    getAll: () => apiRequest('/payroll/deductions', { method: 'GET' }),
    create: (data: any) => apiRequest('/payroll/deductions', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/payroll/deductions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/payroll/deductions/${id}`, { method: 'DELETE' }),
  },

  commissionRules: {
    getAll: (params?: { is_active?: boolean; rule_type?: string }) => {
      const query = buildQueryString(params);
      const url = query ? `/payroll/commission-rules?${query}` : '/payroll/commission-rules';
      return apiRequest(url, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/payroll/commission-rules/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/payroll/commission-rules', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/payroll/commission-rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/payroll/commission-rules/${id}`, { method: 'DELETE' }),
  },

  salarySlips: {
    getAll: (params?: {
      employee_id?: string;
      month?: string;
      slip_type?: string;
      year?: number;
      offset?: number;
      limit?: number;
    }) => {
      const query = buildQueryString(params);
      return apiRequest(`/payroll/slips?${query}`, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/payroll/slips/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/payroll/slips', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/payroll/slips/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/payroll/slips/${id}`, { method: 'DELETE' }),
    verifyPassword: (id: string, password: string) =>
      apiRequest(`/payroll/slips/${id}/verify-password`, {
        method: 'POST',
        body: JSON.stringify({ password })
      }),
    download: async (id: string) => {
      const url = `${API_BASE_URL}/payroll/slips/${id}/download`;
      const tenantId = localStorage.getItem('tenant_id') || localStorage.getItem('selected_tenant_id');
      const response = await fetch(url, {
        credentials: 'include',
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {}
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `salary_slip_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    },
    bulkDownload: async (slip_ids: string[]) => {
      const url = `${API_BASE_URL}/payroll/slips/bulk-download`;
      const tenantId = localStorage.getItem('tenant_id') || localStorage.getItem('selected_tenant_id');
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(tenantId && { 'X-Tenant-ID': tenantId })
        },
        body: JSON.stringify({ slip_ids })
      });
      if (!response.ok) throw new Error('Bulk download failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'salary_slips.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    },
  },
};

// ====================
// SHIFT APIs
// ====================

export const shiftApi = {
  shifts: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/shifts/${query ? '?' + query : ''}`, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/shifts/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/shifts/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/shifts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/shifts/${id}`, { method: 'DELETE' }),
  },

  schedules: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/shifts/schedules?${query}`, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/shifts/schedules/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/shifts/schedules', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/shifts/schedules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  },

  assignments: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/shifts/assignments?${query}`, { method: 'GET' });
    },
    create: (data: any) => apiRequest('/shifts/assignments', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/shifts/assignments/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  },

  swaps: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/shifts/swaps/?${query}`, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/shifts/swaps/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/shifts/swaps', { method: 'POST', body: JSON.stringify(data) }),
    approve: (id: string, reviewNotes?: string) =>
      apiRequest(`/shifts/swaps/${id}/approve${reviewNotes ? `?review_notes=${encodeURIComponent(reviewNotes)}` : ''}`, { method: 'PATCH' }),
    reject: (id: string, reviewNotes?: string) =>
      apiRequest(`/shifts/swaps/${id}/reject${reviewNotes ? `?review_notes=${encodeURIComponent(reviewNotes)}` : ''}`, { method: 'PATCH' }),
    delete: (id: string) => apiRequest(`/shifts/swaps/${id}`, { method: 'DELETE' }),
  },
};

// ====================
// SKILL APIs
// ====================

export const skillApi = {
  types: {
    getAll: () => apiRequest('/skills/types', { method: 'GET' }),
    create: (data: any) => apiRequest('/skills/types', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/skills/types/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/skills/types/${id}`, { method: 'DELETE' }),
  },

  skills: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/skills/?${query}`, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/skills/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/skills/', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/skills/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/skills/${id}`, { method: 'DELETE' }),
  },

  employeeSkills: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/skills/employee-skills?${query}`, { method: 'GET' });
    },
    create: (data: any) => apiRequest('/skills/employee-skills', { method: 'POST', body: JSON.stringify(data) }),
    //new method create for getting bulk skills for employement section
    bulkCreate: (data: any[]) => apiRequest('/skills/employee-skills/bulk', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/skills/employee-skills/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/skills/employee-skills/${id}`, { method: 'DELETE' }),

    /**
     * Get employees who have the specified skills.
     * @param skillIds - Array of skill IDs to filter by
     * @param matchAll - If true, employees must have ALL skills; if false (default), ANY skill matches
     */
    getEmployeesBySkills: (skillIds: string[], matchAll: boolean = false, offset: number = 0, limit: number = 50) => {
      const params = new URLSearchParams();
      skillIds.forEach(id => params.append('skill_ids', id));
      params.append('match_all', String(matchAll));
      params.append('offset', String(offset));
      params.append('limit', String(limit));
      return apiRequest(`/skills/employees-by-skills?${params.toString()}`, { method: 'GET' });
    },
  },
};

// ====================
// TIME & ATTENDANCE APIs
// ====================

export const timeAttendanceApi = {
  timeEntries: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      const endpoint = query ? `/time-attendance/time-entries?${query}` : '/time-attendance/time-entries';
      return apiRequest(endpoint, { method: 'GET' });
    },
    create: (data: any) => apiRequest('/time-attendance/time-entries', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/time-attendance/time-entries/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    approve: (id: string, approved_by: string) =>
      apiRequest(`/time-attendance/time-entries/${id}/approve`, { method: 'POST', body: JSON.stringify({ approved_by }) }),
  },

  attendance: {
    getAll: (params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/time-attendance/attendance?${query}`, { method: 'GET' });
    },
    create: (data: any) => apiRequest('/time-attendance/attendance', { method: 'POST', body: JSON.stringify(data) }),
    getSummary: (employeeId: string, params?: any) => {
      const query = buildQueryString(params);
      return apiRequest(`/time-attendance/attendance/employee/${employeeId}/summary?${query}`, { method: 'GET' });
    },
  },
};

// ====================
// SETTINGS APIs
// ====================

export const settingsApi = {
  system: {
    getAll: () => apiRequest('/settings/system', { method: 'GET' }),
    create: (data: any) => apiRequest('/settings/system', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/settings/system/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  },

  currency: {
    get: () => apiRequest('/settings/currency', { method: 'GET' }),
  },

  notifications: {
    getAll: () => apiRequest('/settings/notifications', { method: 'GET' }),
    create: (data: any) => apiRequest('/settings/notifications', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/settings/notifications/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  },

  locations: {
    getAll: () => apiRequest('/settings/locations', { method: 'GET' }),
    create: (data: any) => apiRequest('/settings/locations', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/settings/locations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  },

  shiftPlanning: {
    get: () => apiRequest('/settings/shift-planning', { method: 'GET' }),
    save: (data: any) => apiRequest('/settings/shift-planning', { method: 'PUT', body: JSON.stringify(data) }),
  },

  customFields: {
    getAll: (params?: { entity_type?: string }) => {
      const query = buildQueryString(params);
      return apiRequest(`/settings/custom-fields?${query}`, { method: 'GET' });
    },
    create: (data: any) => apiRequest('/settings/custom-fields', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/settings/custom-fields/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  },
};

// ====================
// ONBOARDING APIs
// ====================
export const onboardingApi = {
  processes: {
    getAll: (status?: string) => {
      const query = status ? `?status=${status}` : '';
      return apiRequest(`/onboarding/processes${query}`, { method: 'GET' });
    },
    getById: (id: string) => apiRequest(`/onboarding/processes/${id}`, { method: 'GET' }),
    create: (data: any) => apiRequest('/onboarding/processes', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/onboarding/processes/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/onboarding/processes/${id}`, { method: 'DELETE' }),
  },

  tasks: {
    getAll: (processId?: string) => {
      const query = processId ? `?process_id=${processId}` : '';
      return apiRequest(`/onboarding/tasks${query}`, { method: 'GET' });
    },
    create: (data: any) => apiRequest('/onboarding/tasks', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => apiRequest(`/onboarding/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    delete: (id: string) => apiRequest(`/onboarding/tasks/${id}`, { method: 'DELETE' }),
  },
};

// ====================
// ANALYTICS APIs
// ====================

export const analyticsApi = {
  departmentHeadcounts: {
    getAll: (params?: { year?: number; month?: number; department_id?: string }) => {
      const query = buildQueryString(params);
      return apiRequest(`/analytics/department-headcounts?${query}`, { method: 'GET' });
    },

    getLatest: (year?: number) => {
      const currentYear = year || new Date().getFullYear();
      return apiRequest(`/analytics/department-headcounts/latest?year=${currentYear}`, { method: 'GET' });
    },

    getComparison: (currentYear: number, comparisonYear: number) =>
      apiRequest(`/analytics/department-headcounts/compare?current_year=${currentYear}&comparison_year=${comparisonYear}`, { method: 'GET' }),

    createSnapshot: (snapshotDate?: string) => {
      const body = snapshotDate ? JSON.stringify({ snapshot_date: snapshotDate }) : '{}';
      return apiRequest('/analytics/department-headcounts/snapshot', { method: 'POST', body });
    },
  },
};

// ====================
// DASHBOARD APIs
// ====================

export const dashboardApi = {
  getStats: () => apiRequest('/dashboard/stats', { method: 'GET' }),

  getDepartmentComparison: (currentYear?: number, comparisonYear?: number) => {
    const params = buildQueryString({
      current_year: currentYear,
      comparison_year: comparisonYear
    });
    return apiRequest(`/dashboard/department-comparison?${params}`, { method: 'GET' });
  },
};

// Health check
export const healthCheck = () => apiRequest('/health', { method: 'GET' });

// ====================
// UPLOAD APIs
// ====================

export const uploadApi = {
  uploadProfilePhoto: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${API_BASE_URL}/uploads/profile-photo`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  },

  uploadDocument: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${API_BASE_URL}/uploads/document`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Document upload failed' }));
      throw new Error(error.detail || 'Document upload failed');
    }

    return response.json();
  },

  deleteFile: (filename: string) =>
    apiRequest(`/uploads/files/${filename}`, { method: 'DELETE' }),

  getFileUrl: (filename: string) => `${API_BASE_URL}/uploads/files/${filename}`,

  getDocumentUrl: (filename: string) => `${API_BASE_URL}/uploads/documents/${filename}`,
};

export default {
  employee: employeeApi,
  department: departmentApi,
  leave: leaveApi,
  performance: performanceApi,
  payroll: payrollApi,
  shift: shiftApi,
  skill: skillApi,
  timeAttendance: timeAttendanceApi,
  settings: settingsApi,
  analytics: analyticsApi,
  dashboard: dashboardApi,
  upload: uploadApi,
  healthCheck,
};
